/**
 * Backdrop extension for aspect-ratio adaptation.
 *
 * When a slide is adapted to a different output aspect (e.g. 4:5 carousel →
 * 9:16 video), content overlays stay in the centered content box — the social
 * safe zone — and are never moved toward the frame edges. Backdrop bands that
 * bleed off a slide edge (bottom gradient scrims, top color plates) instead
 * STRETCH to the output frame edge they bleed past: the content-side edge
 * keeps its exact position relative to the centered box (so text sitting on
 * the backdrop is undisturbed), while the bleeding edge extends to the frame.
 * In constraint terms the backdrop is pinned to the content on one side and
 * to the frame on the other — a stretch, not a move.
 *
 * A backdrop band = a non-text overlay that spans one axis edge-to-edge and
 * bleeds at least one edge of the other axis with substantial coverage.
 * Overlays spanning both axes are full-canvas layers handled by
 * isBackgroundLayerOverlay (they travel with the cover-scaled background).
 *
 * All classification and stretching happens in VISUAL space: overlays render
 * rotated about their center, so a -90° plate's stored width is its on-screen
 * height (e.g. blue-dynamic-running's bottom plate). Quarter-turn rotations
 * are handled by axis-swapping about the center; oblique rotations never
 * extend — a diagonal band has no clean edge to pin to the frame.
 *
 * NOTE: client-side mirror of server/src/remotion/backdropExtension.ts.
 */

import { AnyOverlay } from '../../models/Post';

export interface BackdropExtension {
  top: boolean;
  bottom: boolean;
  left: boolean;
  right: boolean;
}

// An overlay "touches" an edge when it reaches within this many percent of
// the canvas — the same bleed tolerance as isBackgroundLayerOverlay.
const EDGE_EPSILON = 3;

// Minimum coverage (percent of the free axis) for a band to count as a
// backdrop. Thin strips (rules, dividers) aren't backdrops, and stretching
// them to the frame edge would blow them up.
const MIN_BAND_SIZE = 15;

// Rotations within this many degrees of a multiple of 90° count as aligned.
const ROTATION_EPSILON = 1;

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

type RotationKind = 'axis' | 'quarter' | 'oblique';

function rotationKind(overlay: AnyOverlay): RotationKind {
  const r = (((overlay.rotation ?? 0) % 360) + 360) % 360;
  const nearest = Math.round(r / 90) * 90;
  if (Math.abs(r - nearest) > ROTATION_EPSILON) return 'oblique';
  return nearest % 180 === 0 ? 'axis' : 'quarter';
}

/**
 * On-screen bounding box in percent of the slide, accounting for center
 * rotation. Percent space isn't square, so quarter turns need the slide
 * aspect to swap axes in pixel space. Returns null for oblique rotations.
 */
function getVisualBoxPct(
  overlay: AnyOverlay,
  slideWidth: number,
  slideHeight: number
): Box | null {
  const box: Box = {
    x: overlay.position?.x ?? 0,
    y: overlay.position?.y ?? 0,
    w: overlay.width ?? 0,
    h: overlay.height ?? 0,
  };
  const kind = rotationKind(overlay);
  if (kind === 'oblique') return null;
  if (kind === 'axis') return box;

  const wPx = (box.w / 100) * slideWidth;
  const hPx = (box.h / 100) * slideHeight;
  const cxPx = ((box.x + box.w / 2) / 100) * slideWidth;
  const cyPx = ((box.y + box.h / 2) / 100) * slideHeight;
  const visW = (hPx / slideWidth) * 100;
  const visH = (wPx / slideHeight) * 100;
  return {
    x: (cxPx / slideWidth) * 100 - visW / 2,
    y: (cyPx / slideHeight) * 100 - visH / 2,
    w: visW,
    h: visH,
  };
}

/**
 * Classifies an overlay as an extendable backdrop band. Returns which output
 * frame edges it should extend to, or null for regular content overlays.
 */
export function getBackdropExtension(
  overlay: AnyOverlay,
  slideWidth: number,
  slideHeight: number
): BackdropExtension | null {
  if (overlay.type === 'text' || overlay.type === undefined) return null;
  const box = getVisualBoxPct(overlay, slideWidth, slideHeight);
  if (!box) return null;
  const { x, y, w, h } = box;
  const spansWidth = x <= EDGE_EPSILON && x + w >= 100 - EDGE_EPSILON;
  const spansHeight = y <= EDGE_EPSILON && y + h >= 100 - EDGE_EPSILON;
  if (spansWidth && spansHeight) return null; // full canvas → background layer

  if (spansWidth && h >= MIN_BAND_SIZE) {
    const top = y <= EDGE_EPSILON;
    const bottom = y + h >= 100 - EDGE_EPSILON;
    if (top || bottom) return { top, bottom, left: false, right: false };
  }
  if (spansHeight && w >= MIN_BAND_SIZE) {
    const left = x <= EDGE_EPSILON;
    const right = x + w >= 100 - EDGE_EPSILON;
    if (left || right) return { top: false, bottom: false, left, right };
  }
  return null;
}

export interface AdaptedSlideLayout {
  innerWidth: number;
  innerHeight: number;
  offsetX: number;
  offsetY: number;
  outputWidth: number;
  outputHeight: number;
}

export interface ExtendedBackdropLayout {
  box: { left: number; top: number; width: number; height: number };
  overlay: AnyOverlay;
}

// Per-axis remap in visual space: the box spans from the content-box edge to
// the output edge on extended sides. The visual content-side edge keeps its
// absolute position; the bleeding edge lands at the box edge (preserving any
// original overshoot so the bleed survives).
function extendAxis(
  pos: number,
  size: number,
  extendMin: boolean,
  extendMax: boolean,
  innerSize: number,
  offset: number,
  outputSize: number
): { boxMin: number; boxSize: number; pos: number; size: number } {
  const boxMin = extendMin ? 0 : offset;
  const boxMax = extendMax ? outputSize : offset + innerSize;
  const boxSize = boxMax - boxMin;
  const minPx = extendMin
    ? Math.min(0, (pos / 100) * innerSize)
    : offset + (pos / 100) * innerSize - boxMin;
  const maxPx = extendMax
    ? boxSize + Math.max(0, ((pos + size - 100) / 100) * innerSize)
    : offset + ((pos + size) / 100) * innerSize - boxMin;
  return {
    boxMin,
    boxSize,
    pos: (minPx / boxSize) * 100,
    size: ((maxPx - minPx) / boxSize) * 100,
  };
}

/**
 * Computes the render box and remapped overlay geometry for an extendable
 * backdrop, given the adapted slide layout. The rotation is preserved: for
 * quarter-turn overlays the stretched visual box is converted back to
 * stored (pre-rotation) geometry about the same center.
 */
export function extendedBackdropLayout(
  overlay: AnyOverlay,
  extension: BackdropExtension,
  layout: AdaptedSlideLayout
): ExtendedBackdropLayout {
  // innerWidth/innerHeight are the slide render size, so visual-box percent
  // values carry over unchanged from slide space to the content box.
  const visual = getVisualBoxPct(overlay, layout.innerWidth, layout.innerHeight);
  if (!visual) return { box: { left: layout.offsetX, top: layout.offsetY, width: layout.innerWidth, height: layout.innerHeight }, overlay };

  const horizontal = extendAxis(
    visual.x,
    visual.w,
    extension.left,
    extension.right,
    layout.innerWidth,
    layout.offsetX,
    layout.outputWidth
  );
  const vertical = extendAxis(
    visual.y,
    visual.h,
    extension.top,
    extension.bottom,
    layout.innerHeight,
    layout.offsetY,
    layout.outputHeight
  );

  let stored: Box = {
    x: horizontal.pos,
    y: vertical.pos,
    w: horizontal.size,
    h: vertical.size,
  };
  if (rotationKind(overlay) === 'quarter') {
    // Visual → stored: swap axes in pixel space about the visual center.
    const visWPx = (stored.w / 100) * horizontal.boxSize;
    const visHPx = (stored.h / 100) * vertical.boxSize;
    const cxPx = ((stored.x + stored.w / 2) / 100) * horizontal.boxSize;
    const cyPx = ((stored.y + stored.h / 2) / 100) * vertical.boxSize;
    const wPx = visHPx;
    const hPx = visWPx;
    stored = {
      x: ((cxPx - wPx / 2) / horizontal.boxSize) * 100,
      y: ((cyPx - hPx / 2) / vertical.boxSize) * 100,
      w: (wPx / horizontal.boxSize) * 100,
      h: (hPx / vertical.boxSize) * 100,
    };
  }

  return {
    box: {
      left: horizontal.boxMin,
      top: vertical.boxMin,
      width: horizontal.boxSize,
      height: vertical.boxSize,
    },
    overlay: {
      ...overlay,
      position: { x: stored.x, y: stored.y },
      width: stored.w,
      height: stored.h,
    } as AnyOverlay,
  };
}
